<script lang="ts">
    import { onMount } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';
    import { StatusBadge } from '../../framework/components/app';
    import { briefStatusTone } from './adminListBadges.js';
    import {
        agentSubjectKey,
        getAgentSubjectIcon,
        getAgentSubjectLabel,
    } from './adminListAgents.js';

    type BriefType = 'built_to_brief' | 'illuminator' | 'gapfinder';

    interface AdminBriefRow {
        uuid: string;
        brief_type: BriefType;
        brief_type_label: string;
        name: string;
        user?: {
            uuid: string;
            first_name: string;
            last_name: string;
            email: string;
        } | null;
        status: string;
        progress: number;
        created_at: string;
        updated_at: string;
        view_path: string;
    }

    interface Paging {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number | null;
        to: number | null;
    }

    const DEFAULT_PER_PAGE = 20;
    const PER_PAGE_OPTIONS = [20, 50, 100];

    let briefs: AdminBriefRow[] = [];
    let isLoading = false;
    let searchQuery = '';
    let statusFilter = 'all';
    let typeFilter = 'all';
    let sortField = 'created_at';
    let sortDirection: 'asc' | 'desc' = 'desc';
    let currentPage = 1;
    let perPage = DEFAULT_PER_PAGE;
    let paging: Paging = {
        current_page: 0,
        last_page: 1,
        per_page: DEFAULT_PER_PAGE,
        total: 0,
        from: null,
        to: null
    };

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Executed', label: 'Executed' }
    ];

    const typeOptions = [
        { value: 'all', label: 'All Types' },
        { value: 'built_to_brief', label: 'Built-to-Brief' },
        { value: 'illuminator', label: 'Illuminator' },
        { value: 'gapfinder', label: 'GapFinder' }
    ];

    onMount(async () => {
        await loadBriefs();
    });

    async function loadBriefs() {
        isLoading = true;
        spinner.show();
        try {
            const params: Record<string, string | number> = {
                page: currentPage,
                per_page: Number(perPage) || DEFAULT_PER_PAGE,
                column: sortField,
                order: sortDirection
            };
            if (searchQuery.trim()) params.phrase = searchQuery.trim();
            if (statusFilter !== 'all') params.status = statusFilter;
            if (typeFilter !== 'all') params.type = typeFilter;

            const queryString = Object.keys(params)
                .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
                .join('&');
            const response: any = await api.makeRequest('GET', `/api/v1/admin/briefs?${queryString}`);
            if (response.success) {
                briefs = response.data || [];
                paging = response.paging || paging;
            } else {
                toast.showNotification(false, response.message || 'Failed to load briefs');
                briefs = [];
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading briefs');
            console.error(error);
            briefs = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function handleSort(field: string) {
        if (sortField === field) {
            sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            sortField = field;
            sortDirection = 'desc';
        }
        currentPage = 1;
        loadBriefs();
    }

    function goToPage(page: number) {
        if (page < 1 || page > paging.last_page) return;
        currentPage = page;
        loadBriefs();
    }

    function onSearchApply() {
        currentPage = 1;
        loadBriefs();
    }

    function onStatusChange() {
        currentPage = 1;
        loadBriefs();
    }

    function onTypeChange() {
        currentPage = 1;
        loadBriefs();
    }

    function onPerPageChange() {
        currentPage = 1;
        loadBriefs();
    }

    function formatDate(dateString: string): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString(navigator.language, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    }

    function typeLabel(type: BriefType): string {
        return getAgentSubjectLabel(type);
    }

    function viewItem(item: AdminBriefRow) {
        if (item.brief_type === 'built_to_brief') {
            push(`${item.view_path}?from=briefs`);
            return;
        }
        push(item.view_path);
    }

    function showProgress(item: AdminBriefRow): boolean {
        return item.brief_type === 'built_to_brief' || item.progress > 0;
    }

    let searchDebounceTimer: ReturnType<typeof setTimeout>;
    function debounceSearch() {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = setTimeout(() => onSearchApply(), 300);
    }

    // 1-based page for display (API returns 0-based current_page in paging)
    $: displayPage = (paging.current_page ?? 0) + 1;
</script>

<div class="page-shell admin-list">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">All Briefs</h2>
            <p class="page-subtitle">View and manage all agent work across all users</p>
        </div>
        <div class="header-stats">
            <span class="stat-badge">
                {#if paging.total > 0}
                    Showing {paging.from ?? 0}–{paging.to ?? 0} of {paging.total} brief{paging.total !== 1 ? 's' : ''}
                {:else}
                    0 briefs
                {/if}
            </span>
        </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
        <div class="filters-group">
            <div class="search-box">
                <input
                    type="text"
                    placeholder="Search by brief name, user name, or email..."
                    bind:value={searchQuery}
                    on:input={debounceSearch}
                    class="search-input"
                    aria-label="Search briefs"
                />
            </div>
            <div class="filter-select">
                <label for="admin-brief-type-filter" class="sr-only">Filter by type</label>
                <select id="admin-brief-type-filter" bind:value={typeFilter} on:change={onTypeChange}>
                    {#each typeOptions as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </div>
            <div class="filter-select">
                <label for="admin-brief-status-filter" class="sr-only">Filter by status</label>
                <select id="admin-brief-status-filter" bind:value={statusFilter} on:change={onStatusChange}>
                    {#each statusOptions as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </div>
        </div>
        <div class="per-page-select">
            <label for="admin-brief-per-page">Per page</label>
            <select id="admin-brief-per-page" bind:value={perPage} on:change={onPerPageChange}>
                {#each PER_PAGE_OPTIONS as n}
                    <option value={n}>{n}</option>
                {/each}
            </select>
        </div>
        <span class="results-count">
            {paging.total} {paging.total === 1 ? 'brief' : 'briefs'}
        </span>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading briefs...</div>
    {:else if briefs.length === 0}
        <div class="empty-state">
            <span class="empty-icon"><i class="file text icon" aria-hidden="true"></i></span>
            <p>No briefs found.</p>
            {#if searchQuery || statusFilter !== 'all' || typeFilter !== 'all'}
                <button type="button" class="clear-filters-btn" on:click={() => { searchQuery = ''; statusFilter = 'all'; typeFilter = 'all'; currentPage = 1; loadBriefs(); }}>
                    Clear filters
                </button>
            {/if}
        </div>
    {:else}
        <div class="table-wrap">
            <table class="data-table">
                <thead>
                    <tr>
                        <th class="sortable" on:click={() => handleSort('brief_type')}>
                            <i class="cog icon" aria-hidden="true"></i> Agent
                            {#if sortField === 'brief_type'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('name')}>
                            <i class="file outline icon" aria-hidden="true"></i> Brief
                            {#if sortField === 'name'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('user')}>
                            <i class="user icon" aria-hidden="true"></i> User
                            {#if sortField === 'user'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('status')}>
                            <i class="signal icon" aria-hidden="true"></i> Status
                            {#if sortField === 'status'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('progress')}>
                            <i class="tasks icon" aria-hidden="true"></i> Progress
                            {#if sortField === 'progress'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable" on:click={() => handleSort('created_at')}>
                            <i class="calendar plus icon" aria-hidden="true"></i> Created
                            {#if sortField === 'created_at'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {#each briefs as brief (brief.uuid + brief.brief_type)}
                        {@const statusMeta = briefStatusTone(brief.status)}
                        <tr class="data-row">
                            <td class="type-cell">
                                <div class="type-wrapper">
                                    <div class="type-icon" data-subject={agentSubjectKey(brief.brief_type)}>
                                        <i class="{getAgentSubjectIcon(brief.brief_type)} icon" aria-hidden="true"></i>
                                    </div>
                                    <span class="type-label">{typeLabel(brief.brief_type)}</span>
                                </div>
                            </td>
                            <td class="name-cell">
                                <div class="brief-name">{brief.name || 'Untitled'}</div>
                            </td>
                            <td class="user-cell">
                                {#if brief.user}
                                    <div class="user-name">
                                        {brief.user.first_name || ''} {brief.user.last_name || ''}
                                    </div>
                                    <div class="user-email">{brief.user.email}</div>
                                {:else}
                                    <span class="text-muted">Unknown User</span>
                                {/if}
                            </td>
                            <td>
                                <StatusBadge tone={statusMeta.tone} label={brief.status || 'Unknown'} />
                            </td>
                            <td class="progress-cell">
                                {#if showProgress(brief)}
                                    <div class="progress-bar-container">
                                        <div class="progress-bar" style="width: {brief.progress || 0}%"></div>
                                    </div>
                                    <span class="progress-text">{brief.progress || 0}%</span>
                                {:else}
                                    <span class="text-muted">—</span>
                                {/if}
                            </td>
                            <td class="date-cell">{formatDate(brief.created_at)}</td>
                            <td class="actions-cell">
                                <button
                                    type="button"
                                    class="view-button"
                                    on:click={() => viewItem(brief)}
                                    title="View {brief.brief_type_label}"
                                >
                                    <i class="icon eye" aria-hidden="true"></i>
                                    View
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        {#if paging.last_page > 1 || paging.total > briefs.length}
            <div class="pagination">
                <span class="pagination-info">
                    Showing {paging.from ?? 0}–{paging.to ?? 0} of {paging.total}
                </span>
                <div class="pagination-buttons">
                    <button type="button" class="pagination-btn" disabled={displayPage <= 1} on:click={() => goToPage(displayPage - 1)} aria-label="Previous page">
                        Previous
                    </button>
                    <span class="page-indicator">Page {displayPage} of {paging.last_page}</span>
                    <button type="button" class="pagination-btn" disabled={displayPage >= paging.last_page} on:click={() => goToPage(displayPage + 1)} aria-label="Next page">
                        Next
                    </button>
                </div>
            </div>
        {/if}
    {/if}
</div>

<style>
    .type-cell {
        min-width: 180px;
    }

    .name-cell {
        min-width: 200px;
    }

    .brief-name {
        font-weight: 500;
        color: var(--grey);
    }

    .progress-cell {
        min-width: 120px;
    }
</style>
